import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { saveMultiadapterCredentials } from "@/services/multiadapter/saveMultiadapterCredentials";
import { getMultiadapterCredentials } from "@/services/multiadapter/getMultiadapterCredentials";
import MultiadapterStore from "@/stores/MultiadapterStore";

export function useMultiadapterCredentials() {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const queryClient = useQueryClient();

  // Use the Multiadapter store
  const localUsername = MultiadapterStore(state => state.localUsername);
  const localPassword = MultiadapterStore(state => state.localPassword);
  const localApiUrl = MultiadapterStore(state => state.localApiUrl);
  const hasChanges = MultiadapterStore(state => state.hasChanges);
  const setLocalUsername = MultiadapterStore(state => state.setLocalUsername);
  const setLocalPassword = MultiadapterStore(state => state.setLocalPassword);
  const setLocalApiUrl = MultiadapterStore(state => state.setLocalApiUrl);
  const setOriginalUsername = MultiadapterStore(state => state.setOriginalUsername);
  const setOriginalPassword = MultiadapterStore(state => state.setOriginalPassword);
  const setOriginalApiUrl = MultiadapterStore(state => state.setOriginalApiUrl);

  const { data: credentials, isLoading } = useQuery({
    queryKey: ["multiadapterCredentials"],
    queryFn: getMultiadapterCredentials,
  });

  // Sync store with query data when it changes
  useEffect(() => {
    if (credentials) {
      setOriginalUsername(credentials.username);
      setOriginalPassword(credentials.password);
      setOriginalApiUrl(credentials.apiUrl);
    }
  }, [credentials, setOriginalUsername, setOriginalPassword, setOriginalApiUrl]);

  const { mutate: saveCredentialsMutation, isPending: isSaving } = useMutation({
    mutationFn: () => saveMultiadapterCredentials(localUsername, localPassword, localApiUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["multiadapterCredentials"] });
      // Reset changes after successful save
      setOriginalUsername(localUsername);
      setOriginalPassword(localPassword);
      setOriginalApiUrl(localApiUrl);
    },
  });

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalUsername(e.target.value.trim());
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalPassword(e.target.value);
  };

  const handleApiUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalApiUrl(e.target.value.trim());
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSaveCredentials = () => {
    // Ensure no leading/trailing spaces before saving
    setLocalUsername(localUsername.trim());
    setLocalApiUrl(localApiUrl.trim());
    saveCredentialsMutation();
  };

  return {
    username: localUsername,
    password: localPassword,
    apiUrl: localApiUrl,
    isLoading: isLoading || isSaving,
    showPassword,
    hasChanges,
    handleUsernameChange,
    handlePasswordChange,
    handleApiUrlChange,
    togglePasswordVisibility,
    handleSaveCredentials,
  };
}
